import { useTheme } from '@emotion/react'
import {
    BreachEvent,
    click_origin_breach_screen,
    DataLayerEventName,
    useToggleState,
    ViewSubscriptionItem,
} from '@news-mono/web-common'
import { TheWestSections } from '@west-australian-newspapers/publication-types'
import React, { useEffect } from 'react'
import { getAttribution } from '../../../../../../errors/ErrorPage'
import { useFeatureForSection } from '../../../../../../feature-toggling'
import { LinkContext } from '../../../../../../navigation'
import { useAuthActions } from '../../../../../authentication/AuthenticationProvider'
import { getWeeklyPrice, SubscriptionPackages } from '../../../../common'
import { createLinkClickEvent } from '../../../../createLinkClickHandler'
import SubscriptionPackage from '../../../SubscriptionPackage/SubscriptionPackage'
import { WestRewardsMessage } from '../../shared-components/WestRewardsMessage/WestRewardsMessage'
import { WestRewards } from '../../WestRewards/WestRewards'
import {
    StyledBreachWallContent,
    StyledBreachWallContent2022,
    StyledButton,
    StyledHelpMessage,
    StyledPackage,
    StyledPackage2022,
    StyledRegistrationInfo,
    StyledTerms2022,
    StyledTextLink,
} from './BreachWallContent.styled'
import { DEFAULT_CART_QUANTITY } from '../../../../../..'

export interface BreachWallContentProps {
    title: () => JSX.Element | null
    terms: () => JSX.Element | null
    mediaBanner: () => JSX.Element | null
    subscriptionPackages?: SubscriptionPackages
    breachCreativeName: string
    breachType: string
    breachDeal: string
    onEvent: (event: BreachEvent) => void
}

const backgroundImagePrefix = './thewest-digital-hero-variation-'
export const BreachWallContent: React.FC<BreachWallContentProps> = ({
    title,
    subscriptionPackages,
    breachCreativeName,
    breachType,
    breachDeal,
    onEvent,
    mediaBanner,
    terms,
}) => {
    const renderMediaBanner = mediaBanner
    const renderTitleSection = title
    const renderTerms = terms
    const { onSubscribeClick } = useAuthActions()
    const theme = useTheme()
    const masthead =
        getAttribution(theme.kind, theme.section) || 'The West Australian'

    const isNewDesign2022 = useFeatureForSection({
        feature: 'tp-subscription-new-breach-package-background',
        sections: TheWestSections.default,
    })

    const backgroundVariation =
        useToggleState()?.['tp-subscription-hero-variation'] || 'a'

    const onAllPackagesClicked = React.useCallback(() => {
        createLinkClickEvent(breachCreativeName, breachDeal, onEvent)()
        onSubscribeClick({
            callToAction: click_origin_breach_screen,
        })
    }, [
        breachCreativeName,
        breachDeal,
        onEvent,
        onSubscribeClick,
        createLinkClickEvent,
    ])

    useEffect(() => {
        const viewSubscriptionItems: ViewSubscriptionItem[] =
            subscriptionPackages?.map((pkg, index) => ({
                index,
                item_name: pkg.heading,
                item_id: pkg.rateCode,
                item_brand: masthead,
                component_path: pkg.packagePath,
                item_list_category: 'freemium',
                item_list_category2: 'Premium - Inline',
                item_list_name: breachCreativeName,
                item_list_id: breachType,
                price: getWeeklyPrice(pkg.rateCode, pkg.weeklyPrice),
                quantity: DEFAULT_CART_QUANTITY,
                currency: 'AUD',
            })) || []

        onEvent({
            type: DataLayerEventName.viewSubscriptionItemListEvent,
            parentType: DataLayerEventName.viewItemListEvent,
            originator: 'breachscreen',
            payload: { items: viewSubscriptionItems },
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const breach190Year = breachDeal === '190BD23'
    const breach190YearStyle = breach190Year
        ? 'breach-190-year'
        : 'default-breachwall'

    const ContentContainer = isNewDesign2022
        ? StyledBreachWallContent2022
        : StyledBreachWallContent
    const PackageContainer = isNewDesign2022 ? StyledPackage2022 : StyledPackage
    let largeImage = ''
    let smallImage = ''
    if (isNewDesign2022) {
        try {
            largeImage = require(`${backgroundImagePrefix}${backgroundVariation}.jpg`)
        } catch {
            largeImage = require(`${backgroundImagePrefix}a.jpg`)
        }
        try {
            smallImage = require(`${backgroundImagePrefix}${backgroundVariation}-cropped.jpg`)
        } catch {
            smallImage = require(`${backgroundImagePrefix}a-cropped.jpg`)
        }
    }

    return (
        <>
            <ContentContainer
                theme={theme}
                backgroundColor={isNewDesign2022 ? '#2B2D2E' : undefined}
                largeImage={largeImage}
                smallImage={smallImage}
                className={breach190YearStyle}
            >
                {renderMediaBanner()}
                <StyledRegistrationInfo>
                    {!isNewDesign2022 &&
                        !breach190YearStyle &&
                        renderTitleSection()}
                </StyledRegistrationInfo>
                <PackageContainer breachStyle={breach190YearStyle}>
                    {subscriptionPackages &&
                        subscriptionPackages.map(
                            (subscriptionPackageItem, index) => (
                                <SubscriptionPackage
                                    key={index}
                                    breachCreativeName={breachCreativeName}
                                    breachDeal={breachDeal}
                                    onEvent={onEvent}
                                    packageInfo={subscriptionPackageItem}
                                    masthead={masthead}
                                />
                            ),
                        )}
                    {(isNewDesign2022 || breach190YearStyle) && (
                        <LinkContext.Consumer>
                            {(value) => {
                                const footerLinks = Array.isArray(
                                    value?.links?.footerTermsLinks,
                                )
                                    ? value.links.footerTermsLinks
                                    : value?.links?.footerTermsLinks?.default
                                return (
                                    <StyledTerms2022
                                        breachStyle={breach190YearStyle}
                                    >
                                        See{' '}
                                        <a
                                            href="#"
                                            onClick={onAllPackagesClicked}
                                        >
                                            other subscription options
                                        </a>{' '}
                                        and{' '}
                                        <a
                                            href={
                                                'https://subscriber.thewest.com.au/subscription-terms'
                                            }
                                            target="_blank"
                                        >
                                            Terms &amp; Conditions
                                        </a>
                                    </StyledTerms2022>
                                )
                            }}
                        </LinkContext.Consumer>
                    )}
                </PackageContainer>
                {!isNewDesign2022 && !breach190YearStyle && (
                    <>
                        <StyledButton onClick={onAllPackagesClicked}>
                            View All Packages
                        </StyledButton>
                        <StyledHelpMessage>
                            Need Help? Call us at{' '}
                            <StyledTextLink
                                reverseColor={true}
                                href="tel:1800 811 855"
                            >
                                1800 811 855
                            </StyledTextLink>
                        </StyledHelpMessage>
                        <WestRewardsMessage />
                        {renderTerms()}
                    </>
                )}
            </ContentContainer>
            {(isNewDesign2022 || breach190YearStyle) && <WestRewards />}
        </>
    )
}

BreachWallContent.displayName = 'BreachWallContent'

export default BreachWallContent
